import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useCart } from '../context/CartContext';
import { CONTACT_INFO } from '../data/mock';
import { CheckCircle, Package, Mail, Phone, ArrowRight, CreditCard, Shield, Hash, DollarSign, User } from 'lucide-react';

const OrderConfirmationPage = () => {
  const [orderData, setOrderData] = useState(null);
  const location = useLocation();
  const { clearCart } = useCart();

  useEffect(() => {
    document.title = 'Order Confirmed | Axon Muscle';

    // Case 1: Normal "payment approved immediately" flow — lastOrder already set.
    const storedOrder = sessionStorage.getItem('lastOrder');
    if (storedOrder) {
      try {
        setOrderData(JSON.parse(storedOrder));
      } catch (e) {
        console.error('Error parsing order data:', e);
      }
      sessionStorage.removeItem('lastOrder');
      return;
    }

    // Case 2: 3DS return — SnapPay redirected us back with ?ref=...&oid=...
    // Promote the stashed pendingOrder to a confirmed order.
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    const oid = params.get('oid');
    if (ref || oid) {
      const pending = sessionStorage.getItem('pendingOrder');
      if (pending) {
        try {
          const p = JSON.parse(pending);
          setOrderData({
            orderId: p.orderId || oid || '',
            orderReference: p.orderReference || ref || '',
            transactionId: p.transactionId || '',
            amount: p.amount,
            email: p.email,
            customerName: p.customerName,
            authCode: '',
          });
        } catch (e) {
          console.error('Error parsing pending order:', e);
        }
        sessionStorage.removeItem('pendingOrder');
      } else {
        setOrderData({ orderReference: ref || '', orderId: oid || '' });
      }
      // Clear the cart now that we're back from the bank
      try { clearCart(); } catch (e) { /* ignore */ }
      // Clean the URL so a refresh doesn't re-trigger logic
      try {
        window.history.replaceState({}, document.title, '/order-confirmation');
      } catch (e) { /* ignore */ }
    }
  }, [location.search, clearCart]);

  const orderId = orderData?.orderId || `AXN-${Date.now().toString().slice(-8)}`;
  const displayOrderId = orderId.length > 12 ? orderId.substring(0, 8).toUpperCase() : orderId;

  return (
    <div className="min-h-screen bg-neutral-950">
      <Header />
      
      <main className="pt-[140px] pb-20">
        <div className="max-w-2xl mx-auto px-4 text-center">
          {/* Success Icon */}
          <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg shadow-green-500/20">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
          
          <h1 className="font-display text-3xl md:text-4xl text-white uppercase tracking-tight mb-4">
            Payment Successful!
          </h1>
          
          <p className="text-neutral-400 text-lg mb-8">
            Your order has been placed and payment processed successfully.
            {orderData?.email && (
              <> A confirmation has been sent to <span className="text-axon-500 font-medium">{orderData.email}</span>.</>
            )}
          </p>
          
          {/* Order Details Card */}
          <div className="bg-neutral-900 p-6 md:p-8 rounded-2xl border border-neutral-800 mb-8 text-left">
            <h3 className="font-display font-semibold text-white uppercase tracking-wide mb-6 text-center text-lg">
              Order Details
            </h3>
            
            <div className="space-y-4">
              {/* Order ID */}
              <div className="flex items-center justify-between p-4 bg-neutral-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-axon-600/20 rounded-lg flex items-center justify-center">
                    <Hash className="w-5 h-5 text-axon-500" />
                  </div>
                  <span className="text-neutral-400 text-sm font-medium">Order ID</span>
                </div>
                <span className="font-display text-axon-500 font-bold text-lg">{displayOrderId}</span>
              </div>

              {/* Transaction ID */}
              {orderData?.transactionId && (
                <div className="flex items-center justify-between p-4 bg-neutral-800 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-axon-600/20 rounded-lg flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-axon-500" />
                    </div>
                    <span className="text-neutral-400 text-sm font-medium">Transaction ID</span>
                  </div>
                  <span className="text-white font-mono text-sm">{orderData.transactionId}</span>
                </div>
              )}

              {/* Amount Paid */}
              {orderData?.amount && (
                <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-green-500" />
                    </div>
                    <span className="text-neutral-400 text-sm font-medium">Amount Paid</span>
                  </div>
                  <span className="text-green-400 font-display font-bold text-xl">${orderData.amount.toFixed(2)}</span>
                </div>
              )}

              {/* Customer Name */}
              {orderData?.customerName && (
                <div className="flex items-center justify-between p-4 bg-neutral-800 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-axon-600/20 rounded-lg flex items-center justify-center">
                      <User className="w-5 h-5 text-axon-500" />
                    </div>
                    <span className="text-neutral-400 text-sm font-medium">Customer</span>
                  </div>
                  <span className="text-white font-medium">{orderData.customerName}</span>
                </div>
              )}

              {/* Email */}
              {orderData?.email && (
                <div className="flex items-center justify-between p-4 bg-neutral-800 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-axon-600/20 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-axon-500" />
                    </div>
                    <span className="text-neutral-400 text-sm font-medium">Confirmation Email</span>
                  </div>
                  <span className="text-white font-medium">{orderData.email}</span>
                </div>
              )}

              {/* Auth Code */}
              {orderData?.authCode && (
                <div className="flex items-center justify-between p-4 bg-neutral-800 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-axon-600/20 rounded-lg flex items-center justify-center">
                      <Shield className="w-5 h-5 text-axon-500" />
                    </div>
                    <span className="text-neutral-400 text-sm font-medium">Auth Code</span>
                  </div>
                  <span className="text-white font-mono">{orderData.authCode}</span>
                </div>
              )}
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-neutral-900 p-8 rounded-2xl border border-neutral-800 mb-8 text-left">
            <h3 className="font-display font-semibold text-white uppercase tracking-wide mb-4 flex items-center gap-2">
              <div className="w-8 h-8 bg-axon-600 rounded-lg flex items-center justify-center">
                <Package className="w-4 h-4 text-white" />
              </div>
              What's Next?
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 bg-axon-600 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">1</span>
                <span className="text-neutral-400">You'll receive an email confirmation with your order details shortly.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 bg-axon-600 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">2</span>
                <span className="text-neutral-400">We'll send you tracking information once your order ships.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 bg-axon-600 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">3</span>
                <span className="text-neutral-400">Expected delivery: 3-5 business days.</span>
              </li>
            </ul>
          </div>
          
          {/* Contact Info */}
          <div className="bg-neutral-900 p-6 rounded-2xl border border-neutral-800 mb-8">
            <h3 className="font-display font-semibold text-white uppercase tracking-wide mb-4">Need Help?</h3>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <a 
                href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                className="flex items-center gap-2 text-axon-500 hover:text-axon-400 transition-colors font-medium"
              >
                <Phone className="w-5 h-5" />
                {CONTACT_INFO.phone}
              </a>
              <a 
                href={`mailto:${CONTACT_INFO.email}`}
                className="flex items-center gap-2 text-axon-500 hover:text-axon-400 transition-colors font-medium"
              >
                <Mail className="w-5 h-5" />
                {CONTACT_INFO.email}
              </a>
            </div>
          </div>
          
          {/* CTA */}
          <Link 
            to="/shop"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-axon-600 to-axon-500 hover:from-axon-500 hover:to-axon-400 text-white font-bold uppercase tracking-wider rounded-xl transition-all shadow-button"
          >
            Continue Shopping
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default OrderConfirmationPage;
